import React from 'react'
import classNames from 'classnames'

import ConfiguratorAppContext from '../ConfiguratorAppContext'
import { ProductDataContext } from '../ProductDataContext'

import './ListScreen.scss'
import { generateFullBom, getBomProductsForInflatedItem, PricingParams } from '../bom/bomUtils'
import { inflateListItems } from '../products/productUtils'
import { ConfiguredProductListItem, InflatedListItemProduct, ListRoom } from './ListTypes'
import EmailListPopup from './EmailListPopup'
import emailMyList from '../endpoints/emailMyList'
import SpinnerPopup from '../components/SpinnerPopup'
import AlertPopup from '../components/AlertPopup'
import { useProductPricesWithContext } from '../hooks/useProductPrices'
import {
  getListItemPricing,
  getTotalPriceForBom,
  TotalPriceForBomResult,
} from '../productPricing/productPricingCalculation'
import LoginPopup from './LoginPopup'
import DownloadListPopup from './DownloadListPopup'
import { onDownloadList } from '../utils/downloadList'
import { ProductPriceUnavailable, ProductPricingContext } from '../productPricing/ProductPricingContext'
import { WholesalerListWorkflow } from './WholesalerListWorkflow'
import { ClipspecListWorkflow } from './ClipspecListWorkflow'
import { ListManager } from './ListManager'
import { LoadingState } from '../utils/LoadingState'
import { Alert } from '../utils/Alert'
import { StandardListCtaSection } from './StandardListCtaSection'
import { VersionTag } from '../components/VersionTag'
import { saveSharedListToFirestore } from './SaveSharedListToFirestore'
import { useBuilderMode } from '../useBuilderMode'
import { AnalyticsContext } from '../analytics/AnalyticsContext'
import { Sha256 } from '@aws-crypto/sha256-js'
import { getAnalyticsProductInfo, getEcommercePayloadFrom } from '../analytics/AnalyticsHelpers'

interface Props {
  products: readonly Readonly<ConfiguredProductListItem>[]
  focusedItemUid: string | null
  rooms: readonly ListRoom[]
}

interface BuilderModeContent {
  scrollingFooter: React.ReactNode
  fixedFooter: React.ReactNode
}

const ListScreen: React.FunctionComponent<Props> = ({ products, rooms, focusedItemUid }) => {
  const { userType, listShown, listName, listId } = React.useContext(ConfiguratorAppContext)
  const Products = React.useContext(ProductDataContext)
  const analyticsLogger = React.useContext(AnalyticsContext)

  const builderMode = useBuilderMode()

  const [showLoginPrompt, setShowLoginPrompt] = React.useState(false)

  const [emailListOpen, setEmailListOpen] = React.useState(false)

  const [emailListLoadingState, setEmailListLoadingState] = React.useState<LoadingState | null>(null)
  const [alert, setAlert] = React.useState<Alert | null>(null)

  const [showDownloadListPopup, setShowDownloadListPopup] = React.useState(false)

  const configuredProducts: readonly InflatedListItemProduct[] = inflateListItems(products, Products)

  const pricingContext = React.useContext(ProductPricingContext)
  const { getPackQuantity, isEnabled: shouldShowPricing } = pricingContext

  const fullListBom = React.useMemo(() => {
    return generateFullBom(configuredProducts.map(it => getBomProductsForInflatedItem(it)), getPackQuantity)
  }, [configuredProducts, getPackQuantity])

  const fullListBomItemNos = React.useMemo(() => {
    return fullListBom.map(item => item.itemNo)
  }, [fullListBom])

  const { priceMap, isFetching: isFetchingPricing, priceTypeLabel } = useProductPricesWithContext(shouldShowPricing ? fullListBomItemNos : [], pricingContext)
  const pricingParams: PricingParams = { priceMap, label: priceTypeLabel }

  const totalPrice: TotalPriceForBomResult | null = shouldShowPricing ? getTotalPriceForBom(fullListBom, priceMap) : null

  const onClickDownloadList = () => {
    const pricing = shouldShowPricing ? pricingParams : null
    onDownloadList(inflateListItems(products, Products), getPackQuantity, pricing)
  }

  const analyticsProducts = configuredProducts.map(product => ({
    product: getAnalyticsProductInfo(product.skinProduct),
    quantity: product.quantity,
    price: getListItemPricing(generateFullBom([getBomProductsForInflatedItem(product)], getPackQuantity), priceMap).price?.toString() ?? null
  }))

  const totalQuantity = analyticsProducts.reduce((accum, curr) => {
    return accum + curr.quantity
  }, 0)

  // log view_cart event whenever list changes
  // Simplified to changes in name, id, or item quantity (add / remove)
  // Skips logging while prices are still loading so they can be logged
  React.useEffect(() => {
    if (listShown && !isFetchingPricing) {
      analyticsLogger.log({
        event: 'view_cart',
        ecommerce: getEcommercePayloadFrom(
          analyticsProducts,
          {
            name: listName,
            id: listId
          }
        )
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listShown, isFetchingPricing, totalQuantity, listName, listId])

  const getContent = (): BuilderModeContent => {
    if (builderMode.name === 'clipspec') {
      return {
        scrollingFooter: null,
        fixedFooter: (
          <ClipspecListWorkflow
            clipspecContext={builderMode.clipspecContext}
            configuredProducts={configuredProducts}
          />
        ),
      }

    } else if (builderMode.name === 'wholesaler') {
      return {
        scrollingFooter: null,
        fixedFooter: (
          <WholesalerListWorkflow
            wholesalerContext={builderMode.wholesalerContext}
            onClickDownloadList={onClickDownloadList}
            fullListBom={fullListBom}
            totalPrice={totalPrice?.price ?? ProductPriceUnavailable}
          />
        ),
      }

    } else {
      return {
        scrollingFooter: (
          <StandardListCtaSection
            configuredProducts={configuredProducts}
            fullListBom={fullListBom}
            showAlert={setAlert}
            saveList={() => saveSharedListToFirestore(listName, rooms, configuredProducts)}
            onClickEmailList={() => {
              analyticsLogger.log({
                event: 'cta_click',
                cta_name: 'Email your List',
                cta_link: null,
                cta_category: 'Button',
                cta_location: 'My list'
              })

              setEmailListOpen(true)
            }}
            onClickDownloadList={() => {
              // Log view_cart when list downloaded to record ecommerce items
              analyticsLogger.log({
                event: 'add_to_cart',
                distributor: null,
                add_to_cart_location: 'My list',
                cta_name: 'Download BOM',
                ecommerce: getEcommercePayloadFrom(
                  analyticsProducts,
                  {
                    name: listName,
                    id: listId
                  }
                )
              })

              onClickDownloadList()
            }}
          />
        ),
        fixedFooter: null,
      }
    }
  }

  const builderModeContent = getContent()

  return (
    <div
      className="ListScreen"
      onClick={e => {
        // prevent click-through to shaded background (which dismisses ListScreen)
        e.stopPropagation()
      }}
    >
      <div className="scrollingSection">
        <ListManager
          configuredProducts={configuredProducts}
          rooms={rooms}
          totalPrice={totalPrice}
          focusedItemUid={focusedItemUid}
          pricingParams={pricingParams}
          getPackQuantity={getPackQuantity}
          shouldShowPricing={shouldShowPricing}
          fullListBom={fullListBom}
          isFetchingPricing={isFetchingPricing}
        />

        <div className={classNames('listWorkflowContent', { hidden: !userType })}>
          {builderModeContent.scrollingFooter}
        </div>

        <div style={{ flex: '1 1 0' }} />

        <VersionTag />

      </div>

      {builderModeContent.fixedFooter}

      {/* popups ------ */}

      <>
        {emailListLoadingState && (
          <SpinnerPopup>
            { emailListLoadingState.message}
          </SpinnerPopup>
        )}

        {alert && (
          <AlertPopup
            message={alert.message}
            onClose={() => setAlert(null)}
            buttonLabel={alert.buttonLabel}
          />
        )}

        {emailListOpen && (
          <EmailListPopup
            showOptIn={userType === 'consumer'}
            onClose={() => setEmailListOpen(false)}
            onConfirm={payload => {
              setEmailListOpen(false)
              setEmailListLoadingState({ message: 'Sending your list...' })

              analyticsLogger.log({
                event: 'add_to_cart',
                distributor: null,
                add_to_cart_location: 'My list',
                cta_name: 'Email your List',
                ecommerce: getEcommercePayloadFrom(
                  analyticsProducts,
                  {
                    name: listName,
                    id: listId
                  }
                )
              })

              saveSharedListToFirestore(listName, rooms, configuredProducts)
                .then(id => emailMyList({
                  emailAddress: payload.emailAddress,
                  marketingOptIn: payload.optIn,
                  userType: userType ?? 'consumer',
                  listId: id,
                }))
                .then(_ => {
                  const hash = new Sha256()
                  hash.update(payload.emailAddress)
                  const hashedEmail = hash.digestSync().reduce((accum, curr) =>
                    accum + curr.toString(16).padStart(2, '0')
                  , '')

                  analyticsLogger.log({
                    event: 'send_list',
                    site_location: 'Switch & Socket Builder',
                    user_hashed_email: hashedEmail
                  })

                  setAlert({
                    message: `Your list has been emailed to ${payload.emailAddress}.`,
                    buttonLabel: 'Close'
                  })
                })
                .catch(err => {
                  console.error(err)
                  setAlert({
                    message: 'Something went wrong emailing your list...',
                    buttonLabel: 'Back'
                  })
                })
                .finally(() => {
                  setEmailListLoadingState(null)
                })
            }}
          />
        )}

        {showDownloadListPopup && (
          <DownloadListPopup
            onClose={() => setShowDownloadListPopup(false)}
            onSubmit={(value) => onDownloadList(inflateListItems(products, Products), getPackQuantity, shouldShowPricing ? pricingParams : null, value)}
          />
        )}

        {showLoginPrompt && (
          <LoginPopup
            title="Log in to download Your List"
            onClose={() => { setShowLoginPrompt(false) }}
          />
        )}
      </>

    </div>
  )
}

export default ListScreen
